import React, { useState } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Home.css';
import DownloadBanner from '../components/DownloadBanner';

const Home = () => {
    const { t } = useTranslation();
    const [captcha, setCaptcha] = useState(null);
    const { register, handleSubmit, reset, formState: { errors } } = useForm();

    const handleCaptchaVerify = (response) => {
        setCaptcha(response);
    };

    const onSubmit = async (data) => {
        if (!captcha) {
            return;
        }

        data.captcha = captcha;
        const BACKEND_URL = 'https://thebetteryou.bg/api/v1/contact-mail';

        try {
            await axios.post(BACKEND_URL, data);
        } catch (error) {
            console.error('Error sending mail:', error);
            throw error;
        }

        reset();
        setCaptcha(null);
    };

    return (
        <div className="home-wrapper">
            {/* HERO SECTION */}
            <section id="intro" className="hero-section text-white py-5">
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-7">
                            {/* Pill Badge */}
                            <div className="hero-badge d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill mb-4">
                                <span className="pulse-dot bg-emerald"></span>
                                <span className="badge-text">{t('YOUR PERSONALIZED GROWTH COMPANION') || 'YOUR PERSONALIZED GROWTH COMPANION'}</span>
                            </div>

                            {/* Headline */}
                            <h1 className="hero-title mb-3">
                                {t('Discover who you') || 'Discover who you'}<br />
                                <span className="gradient-text">{t('really are!') || 'really are!'}</span>
                            </h1>

                            {/* Subtitle */}
                            <p className="hero-subtitle mb-4">
                                {t('TheBetterYou helps you take control of your personal development with customized daily tasks, motivational quotes, guided self-reflection, and intelligent progress tracking—all built to help you flourish.') || 'TheBetterYou helps you take control of your personal development with customized daily tasks, motivational quotes, guided self-reflection, and intelligent progress tracking—all built to help you flourish.'}
                            </p>

                            {/* App Store Buttons */}
                            <div className='download-buttons row d-flex justify-content-between align-items-center'>
                                <div className='col'>
                                    <a href="https://apps.apple.com/bg/app/thebetteryou/id6502434628" target="_blank" rel="noopener noreferrer">
                                        <img alt="apple download thebetteryou" src="apple_store_download.jpg" className="img-fluid" />
                                    </a>
                                </div>
                                <div className='col'>
                                    <a href="https://play.google.com/store/apps/details?id=com.thebetteryoumobile" target="_blank" rel="noopener noreferrer">
                                        <img alt="google download thebetteryou" src="play_store_download.jpg" className="img-fluid" />
                                    </a>
                                </div>
                            </div>

                            {/* Ratings / Social Proof */}
                            {/* <div className="pt-3 border-top border-purple-divider d-flex align-items-center gap-3">
                                <div className="d-flex text-warning fs-5">★★★★★</div>
                                <span className="small text-purple-200">
                                    <strong className="text-white fw-bold">4.9/5</strong> {t('from over 12,000 active users') || 'from over 12,000 active users'}
                                </span>
                            </div> */}
                        </div>

                        {/* Right Column: Dual Mockups with Exact Image Links */}
                        <div className="col-lg-5 position-relative d-flex justify-content-center justify-content-lg-end pt-4 pt-lg-0">
                            <div className="hero-mockup-wrapper position-relative d-flex align-items-end justify-content-center">

                                {/* Glowing Aura */}
                                <div className="position-absolute top-0 start-0 w-100 h-100 bg-gradient-brand-glow rounded-5 blur-3xl z-n1 animate-pulse"></div>

                                {/* Left Card: Woman Image */}
                                <div className="portrait-side-card position-relative z-1 pb-2">
                                    <img
                                        src="photo-woman.jpeg"
                                        alt="Happy user holding smartphone and flourishing"
                                        className="w-100 h-auto object-fit-cover rounded-4 shadow-lg border border-white-20 portrait-rotate"
                                    />
                                    <div className="position-absolute bottom-0 start-0 translate-middle-y bg-white text-dark bg-opacity-95 backdrop-blur px-3 py-1.5 rounded-3 shadow border border-purple-100 d-flex align-items-center gap-2" style={{ marginLeft: '-12px' }}>
                                        <span className="pulse-dot bg-emerald"></span>
                                        <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>Growing daily</span>
                                    </div>
                                </div>

                                {/* Right Chassis: Phone Screen App Image */}
                                <div className="phone-side-card position-relative z-2">
                                    <div className="phone-chassis-box bg-black shadow-2xl">
                                        {/* Dynamic Island Notch */}
                                        <div className="phone-notch position-absolute start-50 translate-middle-x z-3"></div>

                                        {/* Inner Screen */}
                                        <div className="phone-screen-box">
                                            <img
                                                src="website_screenshot_redesign.jpg"
                                                alt="TheBetterYou App Screen"
                                                className="phone-screen-img"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* VALUE PILLARS */}
            <section id="benefits" className="value-pillars-section">
                <div className="container">
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm p-4 rounded-4 hover-lift">
                                <div className="pillar-icon bg-amber-light mb-3">🧘</div>
                                <h3 className="h5 fw-bold text-dark">{t('Everything Your Mind Needs') || 'Everything Your Mind Needs'}</h3>
                                <p className="text-secondary small mb-0">{t('Improve your personal growth') || 'Improve your personal growth with tailored tasks and proven psychological techniques.'}</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm p-4 rounded-4 hover-lift">
                                <div className="pillar-icon bg-brand-light mb-3">☀️</div>
                                <h3 className="h5 fw-bold text-dark">{t('Unlock Your Best Days') || 'Unlock Your Best Days'}</h3>
                                <p className="text-secondary small mb-0">{t('Reduce stress and build better habits') || 'Reduce stress and build sustainable daily habits with personalized tasks and reflection.'}</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm p-4 rounded-4 hover-lift">
                                <div className="pillar-icon bg-indigo-light mb-3">🌙</div>
                                <h3 className="h5 fw-bold text-dark">{t('Sleep Like Never Before') || 'Sleep Like Never Before'}</h3>
                                <p className="text-secondary small mb-0">{t('Achieve peace of mind and better sleep') || 'Achieve peace of mind, unburden mental clutter, and unlock restful sleep.'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES SHOWCASE */}
            <section id="features" className="py-5">
                <div className="container">
                    <div className="text-center max-w-2xl mx-auto mb-5">
                        <span className="badge badge-high-contrast bg-purple-badge text-purple-dark rounded-pill px-3 py-2 text-uppercase fw-bold mb-2">{t('Holistic Growth Platform') || 'Holistic Growth Platform'}</span>
                        <h2 className="section-main-title fw-bold text-dark">{t('Discover the Benefits and features') || 'Discover the Benefits and Features'}</h2>
                        <p className="text-secondary">{t('TheBetterYou provides the essential tools') || 'TheBetterYou provides all the essential tools for personal growth: tailored daily tasks, motivational quotes, and an interactive level-up framework.'}</p>
                    </div>

                    <div className="d-flex flex-column gap-5">
                        {/* Feature 1 */}
                        <div id="features-tasks" className="row align-items-center g-5 py-4 border-bottom">
                            <div className="col-md-6 text-center">
                                <img alt="thebetteryou tasks feature" loading="lazy" src="features_tasks.png" className="rounded-4 feature-img" />
                            </div>
                            <div className="col-md-6">
                                <span className="badge badge-high-contrast bg-purple-badge text-purple-dark rounded-pill fw-bold mb-2">01 • {t('Weekly Tailored System') || 'Weekly Tailored System'}</span>
                                <h3 className="feature-title fw-bold text-dark mb-3">{t('Weekly Personalized Tasks') || 'Weekly Personalized Tasks'}</h3>
                                <p className="text-secondary w-75">{t('Get individual tasks tailored') || 'Get individual tasks tailored to your character profile that guide you through your personal growth journey.'}</p>
                            </div>
                        </div>

                        {/* Feature 2 */}
                        <div id="features-level-up" className="row align-items-center g-5 py-4 border-bottom">
                            <div className="col-md-6 order-md-2 text-center">
                                <img alt="thebetteryou level-up feature" loading="lazy" src="features_levelup.png" className="rounded-4 feature-img" />
                            </div>
                            <div className="col-md-6 order-md-1">
                                <span className="badge badge-high-contrast bg-purple-badge text-purple-dark rounded-pill fw-bold mb-2">02 • {t('Gamified Development') || 'Gamified Development'}</span>
                                <h3 className="feature-title fw-bold text-dark mb-3">{t('Profile Level-Up System') || 'Profile Level-Up System'}</h3>
                                <p className="text-secondary w-75">{t('Watch your profile grow') || 'Watch your profile grow as you complete tasks. The level-up system is a visual representation of your progress.'}</p>
                            </div>
                        </div>

                        {/* Feature 3 */}
                        <div id="features-quotes" className="row align-items-center g-5 py-4 border-bottom">
                            <div className="col-md-6 text-center">
                                <img alt="thebetteryou quotes feature" loading="lazy" src="features_quotes.png" className="rounded-4 feature-img" />
                            </div>
                            <div className="col-md-6">
                                <span className="badge badge-high-contrast bg-amber-badge text-amber-dark rounded-pill fw-bold mb-2">03 • {t('Uplifting Motivation') || 'Uplifting Motivation'}</span>
                                <h3 className="feature-title fw-bold text-dark mb-3">{t('Daily Positive Quotes') || 'Daily Positive Quotes'}</h3>
                                <p className="text-secondary w-75">{t('Start each day with an uplifting quote') || 'Start each day with an uplifting quote that inspires you to continue your journey toward self-improvement.'}</p>
                            </div>
                        </div>

                        {/* Feature 4 */}
                        <div id="features-journal" className="row align-items-center g-5 py-4 border-bottom">
                            <div className="col-md-6 order-md-2 text-center">
                                <img alt="thebetteryou journal feature" loading="lazy" src="features_journal.png" className="rounded-4 feature-img" />
                            </div>
                            <div className="col-md-6 order-md-1">
                                <span className="badge badge-high-contrast bg-purple-badge text-purple-dark rounded-pill fw-bold mb-2">04 • {t('Reflection & Memory') || 'Reflection & Memory'}</span>
                                <h3 className="feature-title fw-bold text-dark mb-3">{t('Personal Journal') || 'Personal Journal'}</h3>
                                <p className="text-secondary w-75">{t('Record your daily thoughts') || 'Record your daily thoughts, experiences, and achievements in your personal journal.'}</p>
                            </div>
                        </div>

                        {/* Feature 5 */}
                        <div id="features-worried" className="row align-items-center g-5 py-4 border-bottom">
                            <div className="col-md-6 text-center">
                                <img alt="thebetteryou worried feature" loading="lazy" src="features_worried.png" className="rounded-4 feature-img" />
                            </div>
                            <div className="col-md-6">
                                <span className="badge badge-high-contrast bg-rose-badge text-rose-dark rounded-pill fw-bold mb-2">05 • {t('Emotional SOS') || 'Emotional SOS'}</span>
                                <h3 className="feature-title fw-bold text-dark mb-3">{t('I Am Worried') || 'I Am Worried'}</h3>
                                <p className="text-secondary w-75">{t('Unlock new techniques to manage emotions') || 'Unlock new techniques to manage emotions as you level up. The Give Me Advice feature offers helpful tips.'}</p>
                            </div>
                        </div>

                        {/* Feature 6 */}
                        <div id="features-articles" className="row align-items-center g-5 py-4 border-bottom">
                            <div className="col-md-6 order-md-2 text-center">
                                <img alt="thebetteryou articles feature" loading="lazy" src="features_articles.png" className="rounded-4 feature-img" />
                            </div>
                            <div className="col-md-6 order-md-1">
                                <span className="badge badge-high-contrast bg-purple-badge text-purple-dark rounded-pill fw-bold mb-2">06 • {t('Expert Psychology') || 'Expert Psychology'}</span>
                                <h3 className="feature-title fw-bold text-dark mb-3">{t('Articles') || 'Articles'}</h3>
                                <p className="text-secondary w-75">{t('Read expert psychological articles') || 'Read expert psychological articles. Expand your knowledge and apply insights to your personal growth.'}</p>
                            </div>
                        </div>

                        {/* Feature 7 */}
                        <div id="features-tests" className="row align-items-center g-5 py-4">
                            <div className="col-md-6 text-center">
                                <img alt="psychological tests feature" loading="lazy" src="features_tests.png" className="rounded-4 feature-img" />
                            </div>
                            <div className="col-md-6">
                                <span className="badge badge-high-contrast bg-emerald-badge text-emerald-dark rounded-pill fw-bold mb-2">07 • {t('Self-Knowledge') || 'Self-Knowledge'}</span>
                                <h3 className="feature-title fw-bold text-dark mb-3">{t('Psychological Self-Assessments') || 'Psychological Self-Assessments'}</h3>
                                <p className="text-secondary w-75">{t('in-app self-assessments') || 'Gain insights into your mental health through in-app self-assessments for Anxiety, Depression, ADHD, OCD, and more.'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how_it_works" className="py-5 bg-light border-top border-bottom">
                <div className="container">
                    <div className="text-center max-w-xl mx-auto mb-5">
                        <span className="badge badge-high-contrast bg-purple-badge text-purple-dark rounded-pill px-3 py-2 text-uppercase fw-bold mb-2">{t('Seamless Onboarding') || 'Seamless Onboarding'}</span>
                        <h2 className="section-main-title fw-bold text-dark">{t('Simple 3 Steps to Unlock Your Best Self') || 'Simple 3 Steps to Unlock Your Best Self'}</h2>
                        <p className="text-secondary mt-2">{t('Getting started takes under two minutes') || 'Getting started takes under two minutes. Jump straight into an empowering routine designed explicitly for your mindset.'}</p>
                    </div>

                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm p-4 rounded-4 text-center align-items-center">
                                <div className="step-number bg-brand text-white fw-bold mb-3">1</div>
                                <img alt="thebetteryou signup step" loading='lazy' src="how_it_works_signup.png" className='img-fluid' style={{ width: '10em' }} />
                                <h3 className="h4 fw-bold text-dark">{t('Sign Up') || 'Sign Up'}</h3>
                                <p className="text-secondary small mb-0">{t('Register quickly with your email') || 'Register quickly and securely with your email, Google, or Apple account in just one tap.'}</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm p-4 rounded-4 text-center align-items-center">
                                <div className="step-number bg-brand text-white fw-bold mb-3">2</div>
                                <img alt="thebetteryou test step" loading='lazy' src="how_it_works_quiz.png" className='img-fluid' style={{ width: '8em' }} />
                                <h3 className="h4 fw-bold text-dark">{t('Take the Quiz') || 'Take the Quiz'}</h3>
                                <p className="text-secondary small mb-0">{t('Answer a few questions and discover') || 'Answer a few quick questions to uncover your unique psych profile, stress drivers, and core motivations.'}</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm p-4 rounded-4 text-center align-items-center">
                                <div className="step-number bg-brand text-white fw-bold mb-3">3</div>
                                <img alt="thebetteryou tasks step" loading='lazy' src="how_it_works_tasks.png" className='img-fluid' style={{ width: '10em' }} />
                                <h3 className="h4 fw-bold text-dark">{t('Get Tasks') || 'Get Tasks'}</h3>
                                <p className="text-secondary small mb-0">{t('Receive weekly tasks designed') || 'Receive weekly, customized action tasks designed to systematically build your resilience and character.'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CALL TO ACTION BANNER */}
            <DownloadBanner />

            {/* CONTACT / RECAPTCHA FORM SECTION */}
            {/* <section id="contact-form" className="py-5 bg-light border-top">
                <div className="container">
                    <div className="max-w-xl mx-auto bg-white p-4 p-md-5 rounded-4 shadow-sm border">
                        <h3 className="fw-bold mb-3 text-center">{t('Get in Touch') || 'Get in Touch'}</h3>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="mb-3">
                                <label className="form-label fw-bold">{t('Email address') || 'Email address'}</label>
                                <input 
                                    type="email" 
                                    className="form-control rounded-3" 
                                    {...register("email", { required: true })} 
                                />
                                {errors.email && <span className="text-danger small">{t('Email is required') || 'Email is required'}</span>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label fw-bold">{t('Message') || 'Message'}</label>
                                <textarea 
                                    className="form-control rounded-3" 
                                    rows="4" 
                                    {...register("message", { required: true })}
                                ></textarea>
                                {errors.message && <span className="text-danger small">{t('Message is required') || 'Message is required'}</span>}
                            </div>
                            <div className="mb-3 d-flex justify-content-center">
                                <ReCAPTCHA
                                    sitekey="6Lda9L0qAAAAAJLpYhG0sFfD38x26eR-yWvW3p3s"
                                    onChange={handleCaptchaVerify}
                                />
                            </div>
                            <button type="submit" className="btn btn-primary w-100 py-2.5 rounded-3 fw-bold bg-brand border-0">
                                {t('Send Message') || 'Send Message'}
                            </button>
                        </form>
                    </div>
                </div>
            </section> */}
        </div>
    );
};

export default Home;